// Cigar Explorer View - Browse, filter, and discover cigars
import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  SlidersHorizontal,
  X,
  Clock,
  Star,
  Sparkles
} from 'lucide-react';
import { CigarIcon } from '../components/Icons';
import { CigarDetailModal } from '../components/DailyRitual';
import {
  Cigar,
  CIGARS,
  searchCigars
} from '../data/cigars';

// Primary flavor categories for the wheel
const FLAVOR_CATEGORIES = {
  'Sweet': ['chocolate', 'cocoa', 'dark chocolate', 'honey', 'vanilla', 'caramel', 'molasses', 'sweetness'],
  'Spice': ['pepper', 'black pepper', 'white pepper', 'red pepper', 'light pepper', 'subtle pepper', 'baking spices', 'nutmeg', 'sweet spice'],
  'Earth': ['earth', 'leather', 'oak', 'wood', 'cedar', 'grass', 'hay', 'toast'],
  'Nuts': ['nuts', 'almonds', 'toasted nuts'],
  'Coffee': ['coffee', 'espresso', 'dark roast'],
  'Cream': ['cream', 'creamy', 'light floral', 'subtle citrus', 'citrus'],
  'Fruit': ['dark fruit', 'dried fruit'],
};

interface CigarExplorerViewProps {
  savedCigarIds?: string[];
  onSaveCigar?: (cigar: Cigar) => void;
  onBack?: () => void;
}

export const CigarExplorerView: React.FC<CigarExplorerViewProps> = ({
  savedCigarIds = [],
  onSaveCigar,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStrengths, setSelectedStrengths] = useState<Cigar['strength'][]>([]);
  const [selectedFlavors, setSelectedFlavors] = useState<string[]>([]);
  const [selectedPriceRanges, setSelectedPriceRanges] = useState<Cigar['priceRange'][]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'strength' | 'time' | 'rating'>('name');
  const [selectedCigar, setSelectedCigar] = useState<Cigar | null>(null);
  const [showFlavorWheel, setShowFlavorWheel] = useState(false);

  // Pick up voice search from localStorage (set by App.tsx handleVoiceResult)
  useEffect(() => {
    const voiceSearch = localStorage.getItem('voice_search');
    if (voiceSearch) {
      try {
        const { query, filters } = JSON.parse(voiceSearch);
        if (query) setSearchQuery(query);
        if (filters?.strength) {
          // Map strength values to valid filter values
          const strengthMap: Record<string, Cigar['strength']> = {
            'mild': 'mild',
            'medium': 'medium',
            'full': 'full',
          };
          const mappedStrength = strengthMap[filters.strength];
          if (mappedStrength) {
            setSelectedStrengths([mappedStrength]);
          }
        }
        if (filters?.flavor) {
          setSelectedFlavors([filters.flavor]);
          setShowFlavorWheel(true);
        }
        localStorage.removeItem('voice_search');
      } catch (e) {
        console.error('Failed to parse voice search:', e);
        localStorage.removeItem('voice_search');
      }
    }
  }, []);

  // Filter and sort cigars
  const filteredCigars = useMemo(() => {
    let results = [...CIGARS];

    // Search filter
    if (searchQuery) {
      results = searchCigars(searchQuery);
    }

    // Strength filter
    if (selectedStrengths.length > 0) {
      results = results.filter(c => selectedStrengths.includes(c.strength));
    }

    // Flavor filter
    if (selectedFlavors.length > 0) {
      results = results.filter(c =>
        selectedFlavors.some(f =>
          c.flavorNotes.some(note => note.toLowerCase().includes(f.toLowerCase()))
        )
      );
    }

    // Price range filter
    if (selectedPriceRanges.length > 0) {
      results = results.filter(c => selectedPriceRanges.includes(c.priceRange));
    }

    // Sort
    const strengthOrder = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'];
    switch (sortBy) {
      case 'strength':
        results.sort((a, b) => strengthOrder.indexOf(a.strength) - strengthOrder.indexOf(b.strength));
        break;
      case 'time':
        results.sort((a, b) => a.timeCommitment - b.timeCommitment);
        break;
      case 'rating':
        results.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        results.sort((a, b) => a.brand.localeCompare(b.brand));
    }

    return results;
  }, [searchQuery, selectedStrengths, selectedFlavors, selectedPriceRanges, sortBy]);

  const toggleStrength = (strength: Cigar['strength']) => {
    setSelectedStrengths(prev =>
      prev.includes(strength)
        ? prev.filter(s => s !== strength)
        : [...prev, strength]
    );
  };

  const toggleFlavor = (flavor: string) => {
    setSelectedFlavors(prev =>
      prev.includes(flavor)
        ? prev.filter(f => f !== flavor)
        : [...prev, flavor]
    );
  };

  const togglePriceRange = (range: Cigar['priceRange']) => {
    setSelectedPriceRanges(prev =>
      prev.includes(range)
        ? prev.filter(r => r !== range)
        : [...prev, range]
    );
  };

  const clearFilters = () => {
    setSelectedStrengths([]);
    setSelectedFlavors([]);
    setSelectedPriceRanges([]);
    setSearchQuery('');
  };

  const activeFilterCount = selectedStrengths.length + selectedFlavors.length + selectedPriceRanges.length;

  const strengthColors: Record<Cigar['strength'], string> = {
    'mild': 'bg-green-500',
    'mild-medium': 'bg-lime-500',
    'medium': 'bg-amber-500',
    'medium-full': 'bg-orange-500',
    'full': 'bg-red-500',
  };

  const strengthLabels: Record<Cigar['strength'], string> = {
    'mild': 'Mild',
    'mild-medium': 'Mild-Med',
    'medium': 'Medium',
    'medium-full': 'Med-Full',
    'full': 'Full',
  };

  return (
    <div className="flex-1 overflow-y-auto pb-20">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-stone-900/95 backdrop-blur border-b border-white/10">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-white mb-3">Cigar Explorer</h1>

          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Search cigars, brands, flavors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-amber-500/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Filter Toggle & Sort */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters || activeFilterCount > 0
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-white/10 text-white'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-sm font-medium">Filters</span>
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-stone-900 text-amber-500 text-xs font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setShowFlavorWheel(!showFlavorWheel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFlavorWheel ? 'bg-purple-500 text-white' : 'bg-white/10 text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Flavor Wheel</span>
            </button>

            <div className="flex-1" />

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/10 focus:outline-none"
            >
              <option value="name">A-Z</option>
              <option value="strength">Strength</option>
              <option value="time">Time</option>
              <option value="rating">Rating</option>
            </select>
          </div>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
            {/* Strength Filter */}
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Strength</p>
              <div className="flex flex-wrap gap-2">
                {(['mild', 'mild-medium', 'medium', 'medium-full', 'full'] as Cigar['strength'][]).map(strength => (
                  <button
                    key={strength}
                    onClick={() => toggleStrength(strength)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      selectedStrengths.includes(strength)
                        ? `${strengthColors[strength]} text-white`
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${strengthColors[strength]}`} />
                    {strengthLabels[strength]}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range Filter */}
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Price Range</p>
              <div className="flex flex-wrap gap-2">
                {(['budget', 'moderate', 'premium', 'luxury'] as Cigar['priceRange'][]).map(range => (
                  <button
                    key={range}
                    onClick={() => togglePriceRange(range)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                      selectedPriceRanges.includes(range)
                        ? 'bg-amber-500 text-stone-900'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-amber-500 text-sm font-medium hover:text-amber-400"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Flavor Wheel */}
        {showFlavorWheel && (
          <div className="px-4 pb-4 border-t border-white/10 pt-4">
            <p className="text-white/50 text-xs uppercase tracking-wide mb-3">Select Flavors</p>
            <div className="space-y-3">
              {Object.entries(FLAVOR_CATEGORIES).map(([category, flavors]) => (
                <div key={category}>
                  <p className="text-white/70 text-sm font-medium mb-2">{category}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {flavors.map(flavor => {
                      const isSelected = selectedFlavors.includes(flavor);
                      const count = CIGARS.filter(c =>
                        c.flavorNotes.some(f => f.toLowerCase().includes(flavor.toLowerCase()))
                      ).length;
                      return (
                        <button
                          key={flavor}
                          onClick={() => toggleFlavor(flavor)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            isSelected
                              ? 'bg-purple-500 text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {flavor} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {selectedFlavors.length > 0 && (
              <button
                onClick={() => setSelectedFlavors([])}
                className="mt-3 text-purple-400 text-sm font-medium hover:text-purple-300"
              >
                Clear flavor selection
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="px-4 py-3 border-b border-white/5">
        <p className="text-white/50 text-sm">
          {filteredCigars.length} cigar{filteredCigars.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Cigar Grid */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-1 gap-3">
          {filteredCigars.map(cigar => (
            <CigarCard
              key={cigar.id}
              cigar={cigar}
              onClick={() => setSelectedCigar(cigar)}
            />
          ))}
        </div>

        {filteredCigars.length === 0 && (
          <div className="text-center py-12">
            <CigarIcon className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No cigars match your filters</p>
            <button
              onClick={clearFilters}
              className="mt-4 text-amber-500 font-medium"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedCigar && (
        <CigarDetailModal
          cigar={selectedCigar}
          onClose={() => setSelectedCigar(null)}
          onSave={onSaveCigar}
          isSaved={savedCigarIds.includes(selectedCigar.id)}
        />
      )}
    </div>
  );
};

// Cigar Card Component
interface CigarCardProps {
  cigar: Cigar;
  onClick: () => void;
}

const CigarCard: React.FC<CigarCardProps> = ({ cigar, onClick }) => {
  const strengthColors: Record<Cigar['strength'], string> = {
    'mild': 'bg-green-500/20 text-green-400',
    'mild-medium': 'bg-lime-500/20 text-lime-400',
    'medium': 'bg-amber-500/20 text-amber-400',
    'medium-full': 'bg-orange-500/20 text-orange-400',
    'full': 'bg-red-500/20 text-red-400',
  };

  const strengthLabels: Record<Cigar['strength'], string> = {
    'mild': 'Mild',
    'mild-medium': 'Mild-Med',
    'medium': 'Medium',
    'medium-full': 'Med-Full',
    'full': 'Full',
  };

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-stone-800 flex items-center justify-center flex-shrink-0">
          <CigarIcon className="w-6 h-6 text-amber-500" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-white font-semibold">{cigar.brand}</h3>
              <p className="text-amber-500 text-sm">{cigar.line}</p>
            </div>
            {cigar.rating && (
              <div className="flex items-center gap-1 text-amber-500">
                <Star className="w-4 h-4 fill-current" />
                <span className="text-sm font-medium">{cigar.rating}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${strengthColors[cigar.strength]}`}>
              {strengthLabels[cigar.strength]}
            </span>
            <span className="text-white/40 text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {cigar.timeCommitment} min
            </span>
            <span className="text-white/40 text-xs">
              {cigar.origin}
            </span>
          </div>

          <div className="flex flex-wrap gap-1 mt-2">
            {cigar.primaryFlavors.slice(0, 3).map(flavor => (
              <span key={flavor} className="px-2 py-0.5 rounded bg-white/5 text-white/60 text-xs">
                {flavor}
              </span>
            ))}
          </div>
        </div>
      </div>
    </button>
  );
};

export default CigarExplorerView;
